class SupaBlog {
  constructor() {
    this.searchInput = null;
    this.searchForm = null;
    this.tagButtons = [];
    this.articleItems = [];
    this.featuredArticle = null;
    this.articlesContainer = null;
    this.noResultsMessage = null;
    this.selectedTags = new Set();
    this.searchTerm = '';
    this.blogSection = null;
    this.tagsExpandMedia = null;

    this.init();
  }

  init() {
    const blogSection = document.querySelector('.supa-blog');
    if (!blogSection) return;

    this.blogSection = blogSection;

    this.searchInput = blogSection.querySelector('.supa-blog__search-input');
    this.searchForm = blogSection.querySelector('.supa-blog__search-form');
    this.tagButtons = Array.from(blogSection.querySelectorAll('.supa-blog__tag'));
    this.clearAllButton = blogSection.querySelector('[data-clear-all]');
    this.articleItems = Array.from(blogSection.querySelectorAll('.supa-blog__article-item:not(.supa-blog__article-item--featured)'));
    this.featuredArticle = blogSection.querySelector('.supa-blog__featured');
    this.articlesContainer = blogSection.querySelector('[data-articles-container]');
    this.noResultsMessage = blogSection.querySelector('[data-no-results]');

    this.removeDuplicateTags();
    this.hideSkeleton();
    this.loadStateFromURL();
    this.setupEventListeners();
    this.setupTagsExpandToggle();
    this.setupEmailSignupForm();
    // Ensure featured article is hidden in grid when no filters are active
    this.filterArticles();
  }

  hideSkeleton() {
    const skeleton = document.querySelector('[data-tags-skeleton]');
    const tagsList = document.querySelector('[data-tags-list]');
    if (skeleton) {
      skeleton.style.display = 'none';
    }
    if (tagsList) {
      tagsList.classList.add('loaded');
    }
  }

  removeDuplicateTags() {
    if (!this.tagButtons.length) return;

    const seenTags = new Set();
    const tagsContainer = this.tagButtons[0]?.parentElement;
    if (!tagsContainer) return;

    this.tagButtons.forEach((button) => {
      const tagHandle = button.dataset.tag;
      
      if (seenTags.has(tagHandle)) {
        // Remove duplicate tag button
        button.remove();
      } else {
        seenTags.add(tagHandle);
      }
    });

    // Update tagButtons array after removing duplicates
    this.tagButtons = Array.from(tagsContainer.querySelectorAll('.supa-blog__tag'));
  }

  loadStateFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const searchParam = urlParams.get('search') || '';
    const tagsParam = urlParams.get('tags') || '';

    if (searchParam && this.searchInput) {
      this.searchTerm = searchParam.toLowerCase().trim();
      this.searchInput.value = searchParam;
    }

    if (tagsParam) {
      const tags = tagsParam.split(',').filter(Boolean);
      tags.forEach((tag) => {
        this.selectedTags.add(tag);
        const button = this.tagButtons.find((btn) => btn.dataset.tag === tag);
        if (button) {
          button.classList.add('supa-blog__tag--active');
        }
      });
    }

    if (searchParam || tagsParam) {
      this.filterArticles();
    }

    this.updateClearAllButton();
  }

  updateURL() {
    const url = new URL(window.location);
    
    if (this.searchTerm) {
      url.searchParams.set('search', this.searchTerm);
    } else {
      url.searchParams.delete('search');
    }

    if (this.selectedTags.size > 0) {
      url.searchParams.set('tags', Array.from(this.selectedTags).join(','));
    } else {
      url.searchParams.delete('tags');
    }

    window.history.pushState({}, '', url);
  }

  setupEventListeners() {
    if (this.searchInput) {
      this.searchInput.addEventListener('input', (e) => {
        this.searchTerm = e.target.value.toLowerCase().trim();
        this.filterArticles();
        this.updateURL();
      });

      this.searchInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.filterArticles();
          this.updateURL();
        }
      });
    }

    if (this.searchForm) {
      this.searchForm.addEventListener('submit', (e) => {
        e.preventDefault();
        this.filterArticles();
        this.updateURL();
      });
    }

    this.tagButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.toggleTag(button);
        this.updateURL();
        this.updateClearAllButton();
      });
    });

    if (this.clearAllButton) {
      this.clearAllButton.addEventListener('click', () => {
        this.clearAllTags();
        this.updateURL();
      });
    }
  }

  setupTagsExpandToggle() {
    const expandWrap = this.blogSection?.querySelector('.supa-blog__tags-expand');
    const toggleBtn = this.blogSection?.querySelector('[data-tags-toggle]');
    if (!expandWrap || !toggleBtn) return;

    this.tagsExpandMedia = window.matchMedia('(hover: none), (pointer: coarse)');

    const syncAria = () => {
      const open = expandWrap.classList.contains('supa-blog__tags-expand--open');
      toggleBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      toggleBtn.setAttribute(
        'aria-label',
        open ? 'Collapse the tag list' : 'Expand the tag list'
      );
    };

    toggleBtn.addEventListener('click', (e) => {
      if (!this.tagsExpandMedia.matches) return;
      e.preventDefault();
      expandWrap.classList.toggle('supa-blog__tags-expand--open');
      syncAria();
    });

    this.tagsExpandMedia.addEventListener('change', () => {
      if (!this.tagsExpandMedia.matches) {
        expandWrap.classList.remove('supa-blog__tags-expand--open');
        syncAria();
      }
    });
  }

  clearAllTags() {
    this.selectedTags.clear();
    this.tagButtons.forEach((button) => {
      button.classList.remove('supa-blog__tag--active');
    });
    this.filterArticles();
    this.updateClearAllButton();
  }

  updateClearAllButton() {
    if (this.clearAllButton) {
      if (this.selectedTags.size > 0) {
        this.clearAllButton.style.display = '';
      } else {
        this.clearAllButton.style.display = 'none';
      }
    }
  }

  toggleTag(button) {
    const tag = button.dataset.tag;
    
    if (this.selectedTags.has(tag)) {
      this.selectedTags.delete(tag);
      button.classList.remove('supa-blog__tag--active');
    } else {
      this.selectedTags.add(tag);
      button.classList.add('supa-blog__tag--active');
    }

    this.filterArticles();
  }

  filterArticles() {
    let visibleCount = 0;
    const hasActiveFilters = this.searchTerm || this.selectedTags.size > 0;

    // Hide/show featured section based on active filters
    if (this.featuredArticle) {
      this.featuredArticle.style.display = hasActiveFilters ? 'none' : '';
    }

    // Get featured article item from grid (if exists)
    const featuredArticleItem = this.articlesContainer?.querySelector('.supa-blog__article-item--featured');

    // Sort articles by date before filtering to maintain order
    const sortedItems = Array.from(this.articleItems).sort((a, b) => {
      const dateA = a.dataset.articleDate ? new Date(a.dataset.articleDate) : new Date(0);
      const dateB = b.dataset.articleDate ? new Date(b.dataset.articleDate) : new Date(0);
      return dateB - dateA; // Newest first
    });

    sortedItems.forEach((item) => {
      const articleTitle = item.dataset.articleTitle || '';
      const articleExcerpt = item.dataset.articleExcerpt || '';
      const articleContent = item.dataset.articleContent || '';
      const articleQuickAnswer = item.dataset.articleQuickAnswer || '';
      const articleSources = item.dataset.articleSources || '';
      const articleTags = (item.dataset.articleTags || '').split(',').filter(Boolean);

      const matchesSearch = this.matchesSearch(
        articleTitle,
        articleExcerpt,
        articleContent,
        articleTags,
        articleQuickAnswer,
        articleSources
      );
      const matchesTags = this.matchesTags(articleTags);

      if (matchesSearch && matchesTags) {
        item.style.display = '';
        visibleCount++;
        // Move visible item to maintain order in DOM
        if (this.articlesContainer && item.parentElement === this.articlesContainer) {
          this.articlesContainer.appendChild(item);
        }
      } else {
        item.style.display = 'none';
      }
    });

    // Handle featured article in grid
    if (featuredArticleItem) {
      if (hasActiveFilters) {
        // When filters are active, show featured article if it matches filters
        const articleTitle = featuredArticleItem.dataset.articleTitle || '';
        const articleExcerpt = featuredArticleItem.dataset.articleExcerpt || '';
        const articleContent = featuredArticleItem.dataset.articleContent || '';
        const articleQuickAnswer = featuredArticleItem.dataset.articleQuickAnswer || '';
        const articleSources = featuredArticleItem.dataset.articleSources || '';
        const articleTags = (featuredArticleItem.dataset.articleTags || '').split(',').filter(Boolean);

        const matchesSearch = this.matchesSearch(
          articleTitle,
          articleExcerpt,
          articleContent,
          articleTags,
          articleQuickAnswer,
          articleSources
        );
        const matchesTags = this.matchesTags(articleTags);

        if (matchesSearch && matchesTags) {
          featuredArticleItem.style.display = '';
          visibleCount++;
        } else {
          featuredArticleItem.style.display = 'none';
        }
      } else {
        // When no filters are active, hide featured article from grid (it's shown in featured section)
        featuredArticleItem.style.display = 'none';
      }
    }

    if (this.noResultsMessage) {
      if (visibleCount === 0 && hasActiveFilters) {
        this.noResultsMessage.style.display = 'block';
        if (this.articlesContainer) {
          this.articlesContainer.style.display = 'none';
        }
      } else {
        this.noResultsMessage.style.display = 'none';
        if (this.articlesContainer) {
          this.articlesContainer.style.display = '';
        }
      }
    }
  }

  matchesSearch(title, excerpt, content, articleTags = [], quickAnswer = '', sources = '') {
    if (!this.searchTerm) return true;

    const searchLower = this.searchTerm.toLowerCase();
    const matchesTags = articleTags.some((tag) => tag.includes(searchLower));

    return (
      title.includes(searchLower) ||
      excerpt.includes(searchLower) ||
      content.includes(searchLower) ||
      (quickAnswer?.includes(searchLower)) ||
      (sources?.includes(searchLower)) ||
      matchesTags
    );
  }

  matchesTags(articleTags) {
    if (this.selectedTags.size === 0) return true;

    return Array.from(this.selectedTags).some((selectedTag) =>
      articleTags.includes(selectedTag)
    );
  }

  setupEmailSignupForm() {
    const emailForm = document.querySelector('.supa-blog__email-signup .newsletter-form');
    if (!emailForm) return;

    emailForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      const emailInput = emailForm.querySelector('input[type="email"]');
      const email = emailInput?.value?.trim();
      
      if (!email) return;

      const submitButton = emailForm.querySelector('.supa-blog__email-signup-button');
      const originalButtonText = submitButton?.textContent;
      
      // Disable button during submission
      if (submitButton) {
        submitButton.disabled = true;
        submitButton.textContent = 'Subscribing...';
      }

      try {
        // Submit form to Shopify
        const formData = new FormData(emailForm);
        const response = await fetch(emailForm.action, {
          method: 'POST',
          body: formData,
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });

        const responseText = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(responseText, 'text/html');
        
        // Check if form was successful by looking for success message or checking response
        const isSuccess = response.ok && (
          responseText.includes('newsletter-form__message--success') ||
          responseText.includes('success') ||
          doc.querySelector('.newsletter-form__message--success')
        );

        if (isSuccess) {
          // Send to Klaviyo if available
          if (window._learnq && Array.isArray(window._learnq)) {
            // Update profile in Klaviyo
            window._learnq.push(['identify', {
              $email: email
            }]);
            
            // Track subscription event
            window._learnq.push(['track', 'Subscribe', {
              $email: email,
              source: 'blog_email_signup'
            }]);
            
            console.log('✅ Klaviyo: Sent identify and track events for', email);
          }

          // Show success message
          this.showEmailSignupSuccess(emailForm, emailInput);
        } else {
          // Handle error - check if there's an error message in response
          const errorMsg = doc.querySelector('.newsletter-form__message:not(.newsletter-form__message--success)');
          if (errorMsg) {
            this.showEmailSignupError(emailForm, errorMsg.textContent.trim());
          } else {
            this.showEmailSignupError(emailForm);
          }
        }
      } catch (error) {
        console.error('Email signup error:', error);
        this.showEmailSignupError(emailForm);
      } finally {
        // Re-enable button
        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = originalButtonText || 'Subscribe';
        }
      }
    });
  }

  showEmailSignupSuccess(form, emailInput) {
    // Clear input
    if (emailInput) {
      emailInput.value = '';
    }

    // Hide any existing error messages
    const errorMessage = form.querySelector('.newsletter-form__message:not(.newsletter-form__message--success)');
    if (errorMessage) {
      errorMessage.style.display = 'none';
    }

    // Show success message
    let successMessage = form.querySelector('.newsletter-form__message--success');
    if (!successMessage) {
      successMessage = document.createElement('div');
      successMessage.className = 'newsletter-form__message newsletter-form__message--success form__message';
      successMessage.setAttribute('tabindex', '-1');
      form.appendChild(successMessage);
    }

    successMessage.innerHTML = `
      <span class="svg-wrapper">
        ${document.querySelector('.newsletter-form__message--success .svg-wrapper')?.innerHTML || ''}
      </span>
      Thank you for subscribing!
    `;
    successMessage.style.display = 'flex';
    successMessage.focus();

    // Scroll to success message
    successMessage.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  showEmailSignupError(form, customMessage) {
    // Hide success message if exists
    const successMessage = form.querySelector('.newsletter-form__message--success');
    if (successMessage) {
      successMessage.style.display = 'none';
    }

    // Show error message
    let errorMessage = form.querySelector('.newsletter-form__message:not(.newsletter-form__message--success)');
    if (!errorMessage) {
      errorMessage = document.createElement('small');
      errorMessage.className = 'newsletter-form__message form__message';
      errorMessage.id = 'Newsletter-error--blog';
      const fieldWrapper = form.querySelector('.newsletter-form__field-wrapper');
      if (fieldWrapper) {
        fieldWrapper.appendChild(errorMessage);
      } else {
        form.appendChild(errorMessage);
      }
    }

    const errorText = customMessage || 'Please enter a valid email address.';
    errorMessage.innerHTML = `
      <span class="svg-wrapper">
        ${document.querySelector('.form__message .svg-wrapper')?.innerHTML || '<svg>...</svg>'}
      </span>
      ${errorText}
    `;
    errorMessage.style.display = 'flex';
    errorMessage.setAttribute('aria-live', 'polite');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('.supa-blog')) {
    new SupaBlog();
  }
});
